import React, { useCallback, useEffect, useState } from 'react';
import {
  Users,
  Video,
  HardDrive,
  Activity,
  ScrollText,
  Shield,
  RefreshCw,
  Plus,
  Trash2,
  Server,
  Cpu,
} from 'lucide-react';
import {
  adminService,
  type AdminOverview,
  type AdminLiveStats,
  type SystemLogItem,
  type AdminEntry,
  type AdminUserRow,
} from '../../services/admin.service.ts';

type Tab = 'overview' | 'users' | 'logs' | 'admins';

const USERS_PAGE_SIZE = 20;

function StatCard({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg bg-purple-600/20 flex items-center justify-center">
          <Icon className="w-5 h-5 text-purple-300" />
        </div>
        <span className="text-sm text-gray-400">{label}</span>
      </div>
      <div className="text-2xl font-bold text-white tabular-nums">{value}</div>
      {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    </div>
  );
}

const AdminPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>('overview');
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [live, setLive] = useState<AdminLiveStats | null>(null);
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [userTotal, setUserTotal] = useState(0);
  const [userPage, setUserPage] = useState(1);
  const [userTotalPages, setUserTotalPages] = useState(1);
  const [userQuery, setUserQuery] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [logs, setLogs] = useState<SystemLogItem[]>([]);
  const [logTotal, setLogTotal] = useState(0);
  const [logLevel, setLogLevel] = useState('');
  const [logQuery, setLogQuery] = useState('');
  const [admins, setAdmins] = useState<AdminEntry[]>([]);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminNote, setNewAdminNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const loadOverview = useCallback(async () => {
    setError(null);
    try {
      const [ov, lv] = await Promise.all([
        adminService.getOverview(),
        adminService.getLiveStats(),
      ]);
      setOverview(ov);
      setLive(lv);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Veriler yüklenemedi');
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const data = await adminService.listUsers({
        page: userPage,
        limit: USERS_PAGE_SIZE,
        q: userSearch || undefined,
      });
      setUsers(data.items);
      setUserTotal(data.total);
      setUserTotalPages(data.totalPages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kullanıcılar yüklenemedi');
    }
  }, [userPage, userSearch]);

  const loadLogs = useCallback(async () => {
    try {
      const data = await adminService.getLogs({
        limit: 100,
        level: logLevel || undefined,
        q: logQuery || undefined,
      });
      setLogs(data.items);
      setLogTotal(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Loglar yüklenemedi');
    }
  }, [logLevel, logQuery]);

  const loadAdmins = useCallback(async () => {
    try {
      setAdmins(await adminService.listAdmins());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Admin listesi yüklenemedi');
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    await loadOverview();
    if (tab === 'users') await loadUsers();
    if (tab === 'logs') await loadLogs();
    if (tab === 'admins') await loadAdmins();
    setLoading(false);
  }, [loadOverview, loadUsers, loadLogs, loadAdmins, tab]);

  useEffect(() => {
    void refresh();
    const interval = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      void loadOverview();
    }, 30_000);
    return () => window.clearInterval(interval);
  }, [refresh, loadOverview]);

  useEffect(() => {
    if (tab === 'users') void loadUsers();
    if (tab === 'logs') void loadLogs();
    if (tab === 'admins') void loadAdmins();
  }, [tab, loadUsers, loadLogs, loadAdmins]);

  const handleAddAdmin = async () => {
    if (!newAdminEmail.trim()) return;
    setBusy(true);
    try {
      await adminService.addAdmin(newAdminEmail.trim(), newAdminNote.trim() || undefined);
      setNewAdminEmail('');
      setNewAdminNote('');
      await loadAdmins();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Eklenemedi');
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveAdmin = async (email: string) => {
    if (!window.confirm(`${email} admin listesinden kaldırılsın mı?`)) return;
    setBusy(true);
    try {
      await adminService.removeAdmin(email);
      await loadAdmins();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Silinemedi');
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteUser = async (user: AdminUserRow) => {
    if (
      !window.confirm(
        `${user.name} (${user.email}) kalıcı olarak silinsin mi? Bu işlem geri alınamaz.`
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await adminService.deleteUser(user.id);
      await loadUsers();
      await loadOverview();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kullanıcı silinemedi');
    } finally {
      setBusy(false);
    }
  };

  const disk = overview?.disk.mounts.find((m) => m.totalBytes != null);
  const liveParticipants = live?.totalParticipants ?? overview?.meetings.live ?? 0;
  const liveRooms = live?.activeRooms ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Shield className="w-7 h-7 text-purple-400" />
            Yönetici Paneli
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Sistem metrikleri, canlı toplantılar ve loglar
          </p>
        </div>
        <button
          type="button"
          onClick={() => void refresh()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white text-sm font-medium transition"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Yenile
        </button>
      </div>

      <div className="flex gap-2 border-b border-white/10 pb-2">
        {(
          [
            ['overview', 'Genel bakış'],
            ['users', 'Kullanıcılar'],
            ['logs', 'Sistem logları'],
            ['admins', 'Yetkiler'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              tab === id
                ? 'bg-purple-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          {error}
        </div>
      )}

      {tab === 'overview' && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <StatCard
              icon={Users}
              label="Toplam kullanıcı"
              value={overview?.users.total ?? '—'}
              hint={
                overview
                  ? `Son 7 gün: +${overview.users.last7Days} · 30 gün: +${overview.users.last30Days}`
                  : undefined
              }
            />
            <StatCard
              icon={Video}
              label="Canlı toplantı"
              value={liveRooms}
              hint={`${liveParticipants} aktif katılımcı (Socket)`}
            />
            <StatCard
              icon={HardDrive}
              label="Disk kullanımı"
              value={
                disk?.usedPercent != null ? `%${disk.usedPercent}` : '—'
              }
              hint={
                disk?.usedBytes != null
                  ? `${adminService.formatBytes(disk.usedBytes)} / ${adminService.formatBytes(disk.totalBytes)}`
                  : disk?.error || 'df bilgisi yok'
              }
            />
            <StatCard
              icon={Activity}
              label="Bellek"
              value={
                overview ? `%${overview.host.memory.usedPercent}` : '—'
              }
              hint={
                overview
                  ? `${adminService.formatBytes(overview.host.memory.usedBytes)} / ${adminService.formatBytes(overview.host.memory.totalBytes)}`
                  : undefined
              }
            />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <h2 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Server className="w-4 h-4 text-purple-300" />
                Sunucu performansı
              </h2>
              {overview ? (
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-400">Host</dt>
                    <dd className="text-white text-right">{overview.host.hostname}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-400">Uptime</dt>
                    <dd className="text-white">
                      {adminService.formatUptime(overview.host.uptimeSeconds)}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-400 flex items-center gap-1">
                      <Cpu className="w-3.5 h-3.5" /> CPU
                    </dt>
                    <dd className="text-white text-right max-w-[60%] truncate" title={overview.host.cpuModel}>
                      {overview.host.cpuCount}× · load {overview.host.loadAverage['1m']} /{' '}
                      {overview.host.loadAverage['5m']} / {overview.host.loadAverage['15m']}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-400">Node</dt>
                    <dd className="text-white">{overview.host.nodeVersion}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-400">Process RSS</dt>
                    <dd className="text-white">
                      {adminService.formatBytes(overview.host.process.rssBytes)}
                    </dd>
                  </div>
                  {!overview.meetings.available && (
                    <p className="text-xs text-amber-300/80 pt-2">
                      DB toplantı sayıları sınırlı: {overview.meetings.note}
                    </p>
                  )}
                  {overview.meetings.available && (
                    <div className="flex justify-between gap-4 pt-2 border-t border-white/10">
                      <dt className="text-gray-400">Toplantı (DB)</dt>
                      <dd className="text-white">
                        {overview.meetings.total} toplam · {overview.meetings.live} LIVE
                      </dd>
                    </div>
                  )}
                </dl>
              ) : (
                <p className="text-gray-500 text-sm">Yükleniyor…</p>
              )}
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <h2 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Video className="w-4 h-4 text-purple-300" />
                Aktif odalar
              </h2>
              {!live || live.rooms.length === 0 ? (
                <p className="text-gray-500 text-sm">Şu an canlı oda yok (veya live API yok).</p>
              ) : (
                <ul className="space-y-2 max-h-64 overflow-y-auto">
                  {live.rooms.map((room) => (
                    <li
                      key={room.meetingId}
                      className="rounded-lg bg-black/20 border border-white/5 px-3 py-2"
                    >
                      <div className="flex justify-between text-sm">
                        <span className="font-mono text-purple-200">{room.meetingId}</span>
                        <span className="text-gray-400">{room.participantCount} kişi</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 truncate">
                        {room.participants.map((p) => p.userName).join(', ')}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-xl p-5">
            <h2 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Users className="w-4 h-4 text-purple-300" />
              Son kayıtlar
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-white/10">
                    <th className="pb-2 font-medium">Ad</th>
                    <th className="pb-2 font-medium">E-posta</th>
                    <th className="pb-2 font-medium">Sağlayıcı</th>
                    <th className="pb-2 font-medium">Kayıt</th>
                  </tr>
                </thead>
                <tbody>
                  {(overview?.users.recent ?? []).map((u) => (
                    <tr key={u.id} className="border-b border-white/5 text-gray-300">
                      <td className="py-2.5 text-white">{u.name}</td>
                      <td className="py-2.5">{u.email}</td>
                      <td className="py-2.5">{u.provider || 'email'}</td>
                      <td className="py-2.5">
                        {u.createdAt
                          ? new Date(u.createdAt).toLocaleString('tr-TR')
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {tab === 'users' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              value={userQuery}
              onChange={(e) => setUserQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setUserPage(1);
                  setUserSearch(userQuery.trim());
                }
              }}
              placeholder="Ad veya e-posta ara…"
              className="flex-1 bg-slate-900/80 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder:text-gray-500"
            />
            <button
              type="button"
              onClick={() => {
                setUserPage(1);
                setUserSearch(userQuery.trim());
              }}
              className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium"
            >
              Ara
            </button>
          </div>

          <p className="text-xs text-gray-500">
            Toplam {userTotal} kullanıcı · Sayfa {userPage} / {userTotalPages}
          </p>

          <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-white/10 bg-black/20">
                    <th className="px-4 py-3 font-medium">Ad</th>
                    <th className="px-4 py-3 font-medium">E-posta</th>
                    <th className="px-4 py-3 font-medium">Sağlayıcı</th>
                    <th className="px-4 py-3 font-medium">Kayıt tarihi</th>
                    <th className="px-4 py-3 font-medium text-right">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                        Kullanıcı bulunamadı
                      </td>
                    </tr>
                  ) : (
                    users.map((u) => (
                      <tr key={u.id} className="border-b border-white/5 text-gray-300 hover:bg-white/[0.03]">
                        <td className="px-4 py-3 text-white font-medium">{u.name}</td>
                        <td className="px-4 py-3">{u.email}</td>
                        <td className="px-4 py-3">{u.provider || 'email'}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {u.createdAt
                            ? new Date(u.createdAt).toLocaleString('tr-TR')
                            : '—'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => void handleDeleteUser(u)}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-red-300 hover:bg-red-500/20 text-xs font-medium transition disabled:opacity-40"
                            title="Kullanıcıyı sil"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Sil
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              disabled={userPage <= 1}
              onClick={() => setUserPage((p) => Math.max(1, p - 1))}
              className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Önceki
            </button>
            <span className="text-sm text-gray-400 tabular-nums">
              {(userPage - 1) * USERS_PAGE_SIZE + 1}
              –
              {Math.min(userPage * USERS_PAGE_SIZE, userTotal)} / {userTotal}
            </span>
            <button
              type="button"
              disabled={userPage >= userTotalPages}
              onClick={() => setUserPage((p) => Math.min(userTotalPages, p + 1))}
              className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Sonraki
            </button>
          </div>
        </div>
      )}

      {tab === 'logs' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={logLevel}
              onChange={(e) => setLogLevel(e.target.value)}
              className="bg-slate-900/80 border border-white/10 rounded-xl px-3 py-2 text-sm text-white"
            >
              <option value="">Tüm seviyeler</option>
              <option value="debug">debug</option>
              <option value="info">info</option>
              <option value="warn">warn</option>
              <option value="error">error</option>
            </select>
            <input
              value={logQuery}
              onChange={(e) => setLogQuery(e.target.value)}
              placeholder="Mesajda ara…"
              className="flex-1 bg-slate-900/80 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder:text-gray-500"
            />
            <button
              type="button"
              onClick={() => void loadLogs()}
              className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium"
            >
              Filtrele
            </button>
          </div>
          <p className="text-xs text-gray-500">{logTotal} kayıt</p>
          <div className="bg-slate-950/60 border border-white/10 rounded-xl overflow-hidden">
            <div className="max-h-[28rem] overflow-y-auto font-mono text-xs">
              {logs.length === 0 ? (
                <p className="p-4 text-gray-500">Log yok.</p>
              ) : (
                logs.map((log) => (
                  <div
                    key={log.id}
                    className="px-4 py-2.5 border-b border-white/5 flex gap-3 hover:bg-white/[0.03]"
                  >
                    <span className="text-gray-500 shrink-0 w-36">
                      {new Date(log.createdAt).toLocaleString('tr-TR')}
                    </span>
                    <span
                      className={`shrink-0 w-12 uppercase font-semibold ${
                        log.level === 'error'
                          ? 'text-red-400'
                          : log.level === 'warn'
                            ? 'text-amber-400'
                            : 'text-emerald-400'
                      }`}
                    >
                      {log.level}
                    </span>
                    <span className="text-purple-300/80 shrink-0 w-20 truncate">{log.source}</span>
                    <span className="text-gray-200 flex-1 min-w-0 break-words">{log.message}</span>
                  </div>
                ))
              )}
            </div>
          </div>
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <ScrollText className="w-3.5 h-3.5" />
            Loglar PostgreSQL <code className="text-gray-400">system_logs</code> tablosunda tutulur.
          </p>
        </div>
      )}

      {tab === 'admins' && (
        <div className="space-y-4 max-w-2xl">
          <div className="bg-white/5 border border-white/10 rounded-xl p-5 space-y-3">
            <h2 className="text-white font-semibold">Admin ekle</h2>
            <input
              value={newAdminEmail}
              onChange={(e) => setNewAdminEmail(e.target.value)}
              placeholder="eposta@ornek.com"
              className="w-full bg-slate-900/80 border border-white/10 rounded-xl px-3 py-2 text-sm text-white"
            />
            <input
              value={newAdminNote}
              onChange={(e) => setNewAdminNote(e.target.value)}
              placeholder="Not (opsiyonel)"
              className="w-full bg-slate-900/80 border border-white/10 rounded-xl px-3 py-2 text-sm text-white"
            />
            <button
              type="button"
              disabled={busy}
              onClick={() => void handleAddAdmin()}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
              Ekle
            </button>
          </div>

          <ul className="space-y-2">
            {admins.map((a) => (
              <li
                key={`${a.source}-${a.email}`}
                className="flex items-center justify-between gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="text-white font-medium truncate">{a.email}</p>
                  <p className="text-xs text-gray-500">
                    {a.source === 'env' ? 'ENV (ADMIN_EMAILS)' : 'Veritabanı'}
                    {a.note ? ` · ${a.note}` : ''}
                  </p>
                </div>
                {a.source === 'db' && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void handleRemoveAdmin(a.email)}
                    className="p-2 rounded-lg text-red-300 hover:bg-red-500/20 transition"
                    title="Kaldır"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
